import moment from 'moment';
import { get } from 'lodash';
import Cookies from 'js-cookie';
import theme from '../ui/theme';
import {
  ENTER_KEY,
  ACCOUNT_DROPDOWN,
  SIGN_IN_BUTTON,
  COOKIE_SECURE,
  EXTENSION_REDIRECT_COOKIE_PREFIX,
  BASE_SITE_URL,
} from './constants';

// // String.Includes polyfill for IE 11
// // https://stackoverflow.com/questions/31221341/ie-does-not-support-includes-method
// if (!String.prototype.includes) {
//   String.prototype.includes = function (search, start) {

//     if (typeof start !== 'number') {
//       start = 0;
//     }

//     if (start + search.length > this.length) {
//       return false;
//     }
//     return this.indexOf(search, start) !== -1;

//   };
// }

// // Array.Includes polyfill for IE 11
// // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/includes
// if (!Array.prototype.includes) {
//   Object.defineProperty(Array.prototype, 'includes', {
//     value(searchElement, fromIndex) {

//       // 1. Let O be ? ToObject(this value).
//       if (this == null) {
//         throw new TypeError('"this" is null or not defined');
//       }

//       const o = Object(this);

//       // 2. Let len be ? ToLength(? Get(O, "length")).
//       const len = o.length >>> 0;

//       // 3. If len is 0, return false.
//       if (len === 0) {
//         return false;
//       }

//       // 4. Let n be ? ToInteger(fromIndex).
//       //    (If fromIndex is undefined, this step produces the value 0.)
//       const n = fromIndex | 0;

//       // 5. If n ≥ 0, then
//       //  a. Let k be n.
//       // 6. Else n < 0,
//       //  a. Let k be len + n.
//       //  b. If k < 0, let k be 0.
//       let k = Math.max(n >= 0 ? n : len - Math.abs(n), 0);

//       function sameValueZero(x, y) {
//         return x === y || (typeof x === 'number' && typeof y === 'number' && isNaN(x) && isNaN(y));
//       }

//       // 7. Repeat, while k < len
//       while (k < len) {
//         // a. Let elementK be the result of ? Get(O, ! ToString(k)).
//         // b. If SameValueZero(searchElement, elementK) is true, return true.
//         // c. Increase k by 1.
//         if (sameValueZero(o[k], searchElement)) {
//           return true;
//         }
//         k++;
//       }

//       // 8. Return false
//       return false;
//     }
//   });
// }

const convertToSlug = (str) => (str ? str.replace(/\s/g, '-') : '');

export const makeExternalUrl = (fragment) => {
  if (!fragment) {
    return '';
  }

  if (fragment && fragment.includes) {
    return fragment.includes('http') ? fragment : `https://${fragment}`;
  }
  return '#';
};

export const parseDate = (dateString) => {
  if (dateString) {
    return moment(dateString).format('MMM D[,] YYYY');
  }
  return '';
};

export const isResolvedExternalUrl = (url) => {
  const pattern = /https?:\/\//;
  return pattern.test(url);
};

// ** Matches the following examples
// - example-slug
// - shops/example-slug
export const isSlug = (url) => {
  if (isResolvedExternalUrl(url)) return false;
  let buttonSlug = url.split('/');
  buttonSlug = buttonSlug[buttonSlug.length - 1];
  const pattern = /^[A-Za-z0-9]+(?:-[A-Za-z0-9]+)*$/;
  return pattern.test(buttonSlug);
};

export const attachLocale = (url, locale) => {
  if (!url) {
    return '';
  }

  // do not attach locale to external links
  if (url.includes('http') || url.includes('https') || url.includes('www')) {
    return url;
  }

  // do not attach locale to anchor links (#)
  if (url && url.length && url[0] === '#') {
    return url;
  }

  return `/${locale}${url[0] === '/' ? '' : '/'}${url}`;
};

export const getSignOutUrl = (locale = 'en') => {
  return attachLocale('/signout', locale);
};

export const attachCategorySlug = (slug) => {
  if (!slug) {
    return '';
  }

  return `/categories/${slug}`;
};

export const attachSubcategorySlug = (parentCategorySlug, subCategorySlug) => {
  if (!parentCategorySlug || !subCategorySlug) {
    return '';
  }

  const query = new URLSearchParams();
  query.append('subCategory', subCategorySlug);
  return `/categories/${parentCategorySlug}?${query.toString()}`;
};

export const attachShopSlug = (slug) => {
  if (!slug) {
    return '';
  }

  return `/shops/${slug}`;
};

export const attachSeasonalSlug = (slug) => {
  if (!slug) {
    return '';
  }

  return `/seasonal/${slug}`;
};

export const attachNotFoundSlug = (slug) => {
  if (!slug) {
    return '';
  }

  return `/${slug}`;
};

export const makeExternalCmp = (campaign, channel, tactic, linkItem) => {
  const sadFaces = [
    convertToSlug(campaign),
    convertToSlug(channel),
    convertToSlug(tactic),
    convertToSlug(linkItem),
  ].filter((c) => c).join('-_-');
  if (sadFaces.length) {
    return `ext_cmp=${sadFaces}`;
  }
  return '';
};

export const makeInternalCmp = (id, docket, name, type, creative) => {
  const sadFaces = [
    convertToSlug(id),
    convertToSlug(docket),
    convertToSlug(name),
    convertToSlug(type),
    convertToSlug(creative),
  ].filter((c) => c).join('-_-');
  if (sadFaces.length) {
    return `int_cmp=${sadFaces}`;
  }
  return '';
};

export const trackAnalytics = (parentId, componentName, unTrackedUrl, tags, hash, slug) => {
  const maybeDataTrack = (tags && tags.dataTrack) || '';
  const hashToUse = hash ? `#${hash}` : '';

  const analytics = {
    url: '',
    dataTrackingId: '',
    dataTrack: maybeDataTrack,
  };

  if (!tags) {
    return {
      url: `${unTrackedUrl}${hashToUse}`,
      dataTrackingId: '',
      dataTrack: maybeDataTrack,
    };
  }

  const {
    campaign,
    channel,
    tactic,
    linkItem,
  } = tags;

  const {
    internalCmpId,
    internalCmpDocket,
    internalCmpName,
    internalCmpType,
    internalCmpCreative,
  } = tags;

  const internalSadFaces = makeInternalCmp(internalCmpId, internalCmpDocket, internalCmpName, internalCmpType, internalCmpCreative);
  const externalSadFaces = makeExternalCmp(campaign, channel, tactic, linkItem);

  let trackingUrl = '';
  // internal and external exist
  if (internalSadFaces.length && externalSadFaces.length) {
    trackingUrl = `${internalSadFaces}&${externalSadFaces}`;

    // only external exist
  } else if (internalSadFaces.length === 0 && externalSadFaces.length) {
    trackingUrl = `${externalSadFaces}`;

    // only internal exist
  } else if (internalSadFaces.length) {
    trackingUrl = `${internalSadFaces}`;
  }

  let parameterSymbol = trackingUrl.length === 0 && hashToUse.length === 0 ? '' : '?';
  if (unTrackedUrl && unTrackedUrl.length > 0 && typeof unTrackedUrl === 'string' && unTrackedUrl.indexOf('?') >= 0) {
    parameterSymbol = '&';
  }

  // Do not use makeExternalUrl because it prepends 'http' before the link, conflicting with the base url (KD Jul 10, 2017)
  analytics.url = trackingUrl && hashToUse
    ? `${unTrackedUrl}${parameterSymbol}${trackingUrl}${hashToUse}`
    : unTrackedUrl;
  analytics.dataTrackingId = `${parentId}_${slug ? `${slug}_` : ''}${componentName}`;
  return analytics;
};

export const trackAnalyticsNew = ({
  url,
  location,
  name,
}) => ({
  url,
  dataTrackingId: name,
  dataTrack: location,
});

export const makeDomId = (domName, slug, isMobile = false) => {
  return `${domName}_${slug}${isMobile ? '_mobile' : '_desktop'}`;
};

export const toSlug = (text = '') => {
  return text ? text.trim().replace(/\s+/g, '-').toLowerCase() : null;
};

export const isValidCollectorNumber = (collectorNumber = 'invalid collector') => {
  const collectorNumberPattern = /^[0-9]*$/;
  return collectorNumberPattern.test(collectorNumber);
};

export const canUseDom = () => {
  return !!(
    (typeof window !== 'undefined'
      && window.document && window.document.createElement)
  );
};

export const sortAlphabetically = (a, b) => {
  const nameA = a && a.name ? a.name.toUpperCase() : '';
  const nameB = b && b.name ? b.name.toUpperCase() : '';
  if (nameA < nameB) {
    return -1;
  }
  if (nameA > nameB) {
    return 1;
  }
  return 0;
};

export const setWindowHash = (...hashTexts) => {
  if (!window || !hashTexts) {
    return;
  }

  window.location.hash = hashTexts
    .filter((text) => (typeof text) === 'string')
    .join('-_-');
};

export const scrollJump = (anchorId) => {
  if (window?.document) {
    window.document.getElementById(anchorId).scrollIntoView();
  }
};

// Input paramter is coming from window.location.hash
// Input starts with '#'
export const sanitizeHash = (windowHash) => {
  const hashData = (windowHash).replace('#', '');
  let sanitizedHash = hashData;
  const questionMarkIndex = hashData.indexOf('?');
  if (questionMarkIndex !== -1) {
    sanitizedHash = hashData.slice(0, questionMarkIndex);
  }
  return sanitizedHash;
};

export const redirectToUrl = (url) => {
  if (!window?.location || !url) {
    return;
  }

  window.location.href = url;
};

export const isMobile = () => {
  let width = Infinity;
  if (window?.document) {
    width = Math.max(window.document.documentElement.clientWidth, window.innerWidth || 0);
  }
  const deviceBreakpoint = theme.breakpoints.m[0];
  const thisIsMobile = width < deviceBreakpoint;
  return thisIsMobile;
};

export const replaceSpaceWithPercent20 = (string = '') => {
  const removedSpace = string.replace(/\s/g, '%20');
  return removedSpace;
};

export const isEnter = (event) => {
  let isEvent = false;
  if (event.key === ENTER_KEY) {
    isEvent = true;
  } else if (event.nativeEvent && event.nativeEvent.key === ENTER_KEY) {
    isEvent = true;
  }
  return isEvent;
};

export const findLetterFromShop = (shop) => {
  if (shop) {
    if (shop.displayName && shop.displayName.length) {
      return shop.displayName[0].toUpperCase();
    } if (shop.name && shop.name.length) {
      return shop.name[0].toUpperCase();
    }
  }
  return null;
};

export const switchLocale = (url = '', locale = 'en') => {
  return url.replace(/(en|fr)/, locale);
};

export const URL_STATE_CHANGE_EVENT = 'shops:urlchange';

export const getCurrentRelativeUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
};

export const replaceBrowserUrl = (url) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.history.replaceState({}, '', url);
  window.dispatchEvent(new CustomEvent(URL_STATE_CHANGE_EVENT, {
    detail: {
      url: getCurrentRelativeUrl(),
    },
  }));
};

export const resolveLanguageSwitchUrl = ({
  currentPath = '',
  targetLocale = 'en',
  localeUrls = {},
  subCategoryLocaleSlugMap = {},
}) => {
  const baseUrl = localeUrls[targetLocale] || switchLocale(currentPath, targetLocale);

  if (!baseUrl) {
    return baseUrl;
  }

  const currentUrl = new URL(currentPath, BASE_SITE_URL);
  const selectedSubCategorySlugs = currentUrl
    .searchParams
    .getAll('subCategory')
    .filter(Boolean);
  const targetUrl = new URL(baseUrl, BASE_SITE_URL);

  targetUrl.hash = currentUrl.hash;

  if (!Object.keys(subCategoryLocaleSlugMap).length) {
    return targetUrl.pathname.includes('/categories/')
      ? `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`
      : baseUrl;
  }

  if (!selectedSubCategorySlugs.length) {
    return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
  }

  const sourceSlugToTargetSlug = Object.values(subCategoryLocaleSlugMap).reduce((acc, localeSlugs) => {
    if (!localeSlugs || !localeSlugs[targetLocale]) {
      return acc;
    }

    Object.values(localeSlugs).forEach((sourceSlug) => {
      if (sourceSlug) {
        acc[sourceSlug] = localeSlugs[targetLocale];
      }
    });

    return acc;
  }, {});

  targetUrl.searchParams.delete('subCategory');

  selectedSubCategorySlugs
    .map((slug) => sourceSlugToTargetSlug[slug])
    .filter(Boolean)
    .forEach((slug) => targetUrl.searchParams.append('subCategory', slug));

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
};

// [JD/FK] - used for site map components only 9/28/2017
export const extractSiteMapLinks = (links = []) => {
  return links.reduce((results, slot) => {
    const {
      url = '',
      name = '',
      text = '',
      navSlotPurpose = '',
      tags = {},
      descriptiveText,
    } = slot;

    const link = {};

    link.url = url;
    link.name = text || name;
    link.tags = tags;
    link.navSlotPurpose = navSlotPurpose;
    link.descriptiveText = descriptiveText;

    // Need to omit links to Sign In/Out and Account Nav Slot
    if (navSlotPurpose === ACCOUNT_DROPDOWN || navSlotPurpose === SIGN_IN_BUTTON) {
      return [];
    }

    results.push(link);

    if (slot.dropdownEntries && slot.dropdownEntries.length) {
      const downdownLinks = extractSiteMapLinks(slot.dropdownEntries);
      results.push(...downdownLinks);
    }

    return results;
  }, []);
};

export const genericProtocol = (url) => {
  return !process.browser ? url : url.replace(/^(https?):\/\//i, '//');
};

export const resolveInterstitialURI = ({ shopSlug, offerSlug = '', adSlug = '', locale = 'en' }) => {
  const resolvedUrl = attachLocale('/redirect', locale);
  const redirectShopSlug = shopSlug || '';
  let externalRedirectUrl = `${resolvedUrl}?shopSlug=${redirectShopSlug}`;

  if (offerSlug.length > 0) {
    externalRedirectUrl += `&offerSlug=${offerSlug}`;
  }

  if (adSlug.length > 0) {
    externalRedirectUrl += `&adSlug=${adSlug}`;
  }

  return { externalRedirectUrl };
};

// eslint-disable-next-line no-undef
export const isChrome = () => typeof chrome !== 'undefined' && chrome.runtime && chrome.runtime.sendMessage;

export const isBrowser = () => typeof window !== 'undefined';

export const accessKeyHandler = (event, handler) => {

  switch (event.which) {
    case 13:
    case 32: {
      event.stopPropagation();
      return handler();
    }
    default:
      return false;
  }
};

const getPrep = (prep, key) => {
  let p = prep;
  try {
    p = JSON.parse(prep);
  } catch (error) {
    p = prep;
  }
  return get(p, [key], get(p, ['0', key], ''));
};

export const resolveAccessibleLabel = (overrideText, shop, p, pk1, actionText, label, pk2) => {

  if (overrideText) {
    return overrideText;
  }

  if (actionText) {
    if (shop && shop.displayName) {
      return `${actionText} ${getPrep(p, pk1 || 'at1')} ${shop.displayName} ${label && pk2 ? [getPrep(p, pk2), label].join(' ') : ''}`;
    }
    return actionText;
  }
  return null;
};

export const screenReaderTextReplace = (text, locale, pad = true) => {

  const times = {
    en: ' times',
    fr: ' fois',
  };
  const pattern = pad ? / (\d+)x / : /(\d+)x/;
  const replacement = (times[locale] ? times[locale] : 'x') + (pad ? ' ' : '');
  return text && text
    .replace(pattern, `${pad ? ' ' : ''}$1${replacement}`)
    .replace(/\*/g, '');
};

export const extensionRedirect = (slug, environment = 'production') => {
  if (!slug) {
    return;
  }

  const cookieName = `${EXTENSION_REDIRECT_COOKIE_PREFIX}-${environment}`;

  const retailerRedirects = (() => {
    try {
      return JSON.parse(Cookies.get(cookieName));
    } catch (err) {
      console.error(err);
      return {};
    }
  })();

  retailerRedirects[slug] = moment().unix();

  const cookieOptions = {
    path: '/',
    secure: Boolean(COOKIE_SECURE),
  };
  Cookies.set(cookieName, JSON.stringify(retailerRedirects), cookieOptions);
};

export const getAnchorButtonUrlProps = (
  buttonUrl,
  locale,
) => {
  let directLink = false;
  let resolvedUrl = null;

  if (isSlug(buttonUrl)) {
    resolvedUrl = attachLocale(buttonUrl, locale);
  } else {
    resolvedUrl = makeExternalUrl(buttonUrl);
    directLink = true;
  }

  return {
    url: resolvedUrl,
    directLink,
  };
};

export const hasTargetingConsentForPPID = () => {
  const optanonConsentCookie = Cookies.get('OptanonConsent');

  if (!optanonConsentCookie) {
    return false;
  }

  try {
    const consentParams = new URLSearchParams(optanonConsentCookie);
    const groupsValue = consentParams.get('groups') || '';
    return /(^|,)C0004:1($|,)/.test(groupsValue);
  } catch (error) {
    return false;
  }
};

export const getInitAdSlotsScript = (pageAds, ppid = '') => {
  const definedSlots = pageAds.map((ad, index) => {
    // Fallback for size mappings if JSON parsing fails or sizeMappings is missing
    let sizeMappings;
    try {
      sizeMappings = JSON.parse(ad.adSlot.sizeMappings);
    } catch (error) {
      console.error('Error parsing size mappings for ad:', ad, error);
      sizeMappings = [];
    }

    // Check if required fields (path, id, sizeMappings) exist
    if (!ad.active || !ad.path || !ad.id || !sizeMappings.length) {
      console.warn('Missing path, id, or size mappings for ad');
      return ''; // Skip this ad slot if data is incomplete
    }

    const flatSizeMapping = sizeMappings.map(([media, size]) => size);

    // Create the output string for this slot with the responsive mapping
    const outputString = `
      const responsiveAdSlot${index} = googletag
        .defineSlot(
          '${ad.path}',
          ${JSON.stringify(flatSizeMapping)},
          '${ad.id}'
        )
        .addService(googletag.pubads());

        const mapping${index} = googletag
          .sizeMapping()
          ${sizeMappings
            .map(([media, size]) => (
              `.addSize(${JSON.stringify(media)}, ${JSON.stringify(size)})`
            ))
    .join('')}
          .build();

        responsiveAdSlot${index}.defineSizeMapping(mapping${index});
        ${Number.isInteger(ad.targetingPosition)
          ? `responsiveAdSlot${index}.setConfig({ targeting: { pos: '${ad.targetingPosition}' } });`
          : ''
        }

    `;

    return outputString;
  }).filter(Boolean); // Filter out empty strings from invalid ads

  if (!definedSlots.length) {
    return '';
  }

  return `
    window.googletag = window.googletag || { cmd: [] };

    googletag.cmd.push(function() {
      ${definedSlots.join('')}
      ${ppid && hasTargetingConsentForPPID()
        ? `googletag.pubads().setPublisherProvidedId('${ppid}');`
        : ''
      }
      googletag.enableServices();

      window.wasGoogletagInitialized = true;
      document.dispatchEvent(new Event('googletagInitialized'));
    })
  `;
};

export const getSignInUrl = (locale = 'en') => {
  const url = new URL(`/${locale}/signin`, BASE_SITE_URL);

  return url.toString();
};

export const getSigninUrlWithOrigin = ({
  currentPath,
  signInUrl,
}) => {
  const url = new URL(signInUrl, BASE_SITE_URL);

  if (currentPath) {
    let relativePath = '';

    try {
      const parsed = new URL(currentPath, BASE_SITE_URL);

      // Ensure it's same-origin before using pathname
      if (parsed.origin === BASE_SITE_URL) {
        relativePath = parsed.pathname + parsed.search + parsed.hash;
      }
    } catch (e) {
      // If it's not a valid absolute URL, treat it as a relative one
      relativePath = currentPath;
    }
    // Only include if path is safe
    const isSafeRelativePath = relativePath.startsWith('/')
      && !relativePath.startsWith('//')
      && !relativePath.includes('://')
      && !relativePath.toLowerCase().startsWith('/javascript:')
      && !relativePath.includes('\\');

    if (isSafeRelativePath) {
      url.searchParams.set('origin', relativePath);
    }
  }

  return url.toString();
};

export const formatNumber = (value, delimiter = ',') => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, delimiter);
};
